import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Alert,
  Container,
  Card,
  CardContent,
  Typography,
  Avatar,
  Paper,
  Grid,
  Divider,
  Chip,
  Fade,
  Slide
} from '@mui/material';
import {
  Lock as LockIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Group as GroupIcon,
  VerifiedUser as VerifiedUserIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import api from '../services/api';
import { BRAND } from '../config/brand';

interface LoginProps {
  onLoginSuccess: (token: string, user: any) => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/auth/login', formData);
      const { token, username, fullName, role, userId, departments } = response.data;
      
      // 保存token到localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify({ username, fullName, role, userId, departments }));
      
      // 设置axios默认headers
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      onLoginSuccess(token, { username, fullName, role, userId, departments });
      
      // 登录成功后重定向到仪表盘
      navigate('/');
    } catch (error: any) {
      console.error('登录失败:', error);
      setError(error.response?.data?.message || '登录失败，请检查用户名和密码');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const features = [
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: '#2563EB' }} />,
      title: '权限管控',
      description: '多部门细粒度权限控制，数据安全隔离'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: '#2e7d32' }} />,
      title: 'AI 驱动',
      description: 'DeepSeek AI 智能问答，秒级精准检索'
    },
    {
      icon: <GroupIcon sx={{ fontSize: 40, color: '#ed6c02' }} />,
      title: '现代架构',
      description: 'React + SpringBoot 前后端分离架构'
    }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0F172A 0%, #1E3A8A 50%, #2563EB 100%)',
        display: 'flex',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          {/* 左侧介绍区域 */}
          <Grid item xs={12} md={6}>
            <Slide direction="right" in={true} timeout={1000}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: 3,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                {/* 公司Logo和标题 */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      background: 'linear-gradient(45deg, #2563EB, #60A5FA)',
                      boxShadow: '0 8px 32px rgba(37, 99, 235, 0.3)'
                    }}
                  >
                    <BusinessIcon sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                      fontWeight: 700,
                      background: 'linear-gradient(45deg, #2563EB, #6366F1)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1
                    }}
                  >
                    {BRAND.systemName}
                  </Typography>
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    sx={{ fontWeight: 500, mb: 3 }}
                  >
                    {BRAND.tagline}
                  </Typography>
                  <Chip
                    label="React + SpringBoot + DeepSeek AI 驱动"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '1rem', py: 1 }}
                  />
                </Box>

                {/* 公司介绍 */}
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: 1.8,
                      color: 'text.secondary',
                      textAlign: 'justify',
                      mb: 3
                    }}
                  >
                    本项目基于 React + SpringBoot + DeepSeek 开发的创新公司多部门权限管理知识库。
                    采用现代化前后端分离架构，集成 DeepSeek AI 大模型能力，
                    为企业提供智能化的知识管理和检索解决方案。
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: 1.8,
                      color: 'text.secondary',
                      textAlign: 'justify'
                    }}
                  >
                    系统支持细粒度的多部门权限控制，确保不同部门只能访问授权的知识内容。
                    通过 AI 智能问答功能，员工可以快速获取精准答案，大幅提升工作效率。
                  </Typography>
                </Box>

                {/* 功能特色 */}
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}
                  >
                    系统特色
                  </Typography>
                  <Grid container spacing={2}>
                    {features.map((feature, index) => (
                      <Grid item xs={12} sm={4} key={index}>
                        <Box
                          sx={{
                            textAlign: 'center',
                            p: 2,
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-5px)',
                              boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          {feature.icon}
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}
                          >
                            {feature.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: '0.875rem' }}
                          >
                            {feature.description}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>
            </Slide>
          </Grid>

          {/* 右侧登录区域 */}
          <Grid item xs={12} md={6}>
            <Fade in={true} timeout={1500}>
              <Card
                elevation={24}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: 'rgba(255, 255, 255, 0.98)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #2563EB, #60A5FA, #6366F1)',
                    borderRadius: '3px 3px 0 0'
                  }
                }}
              >
                {/* 登录标题 */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      mx: 'auto',
                      mb: 2,
                      background: 'linear-gradient(45deg, #2563EB, #60A5FA)',
                      boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)'
                    }}
                  >
                    <VerifiedUserIcon />
                  </Avatar>
                  <Typography
                    variant="h4"
                    component="h2"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    欢迎登录
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    请输入您的账户信息
                  </Typography>
                </Box>

                {/* 登录表单 */}
                <Box component="form" onSubmit={handleSubmit}>
                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                          fontSize: '1.5rem'
                        }
                      }}
                    >
                      {error}
                    </Alert>
                  )}
                  
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="username"
                    label="用户名"
                    name="username"
                    autoComplete="username"
                    autoFocus
                    value={formData.username}
                    onChange={handleInputChange('username')}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#2563EB',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2563EB',
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <PersonIcon sx={{ mr: 1.5, color: 'action.active' }} />
                      ),
                    }}
                  />
                  
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="密码"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#2563EB',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2563EB',
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <LockIcon sx={{ mr: 1.5, color: 'action.active' }} />
                      ),
                    }}
                  />
                  
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? null : <LoginIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(45deg, #2563EB, #60A5FA)',
                      boxShadow: '0 4px 20px rgba(37, 99, 235, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #1D4ED8, #2563EB)',
                        boxShadow: '0 6px 25px rgba(37, 99, 235, 0.4)',
                        transform: 'translateY(-2px)'
                      },
                      '&:disabled': {
                        background: 'linear-gradient(45deg, #bdbdbd, #e0e0e0)',
                        boxShadow: 'none',
                        transform: 'none'
                      }
                    }}
                  >
                    {loading ? '登录中...' : '登录系统'}
                  </Button>
                </Box>

                {/* 底部信息 */}
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Divider sx={{ mb: 2 }}>
                    <Chip label="安全登录" size="small" />
                  </Divider>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    {BRAND.copyright}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    基于 React + SpringBoot + DeepSeek AI 的智能知识库
                  </Typography>
                </Box>
              </Card>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Login;
